import { useCallback, useState } from 'react';
import { Navigate } from 'react-router';
import { useAuth0 } from '@auth0/auth0-react';

import Config from 'Config';
import { useAuth0Config } from 'Auth0ProviderWithConfig';
import RefreshOverlay from 'common/components/RefreshOverlay';
import { fetchSessionRedirect, isLoggedIn } from 'common/services/AuthService';
import { useAppSelector } from 'common/stores/redux/hooks';
import { State } from 'common/stores/redux/Authentication/interfaces';
import MotiliButton from 'common/components-v2/buttons/MotiliButton';
import Alert from 'common/components/Alert';
import * as logger from 'common/utils/Logger';
import styles from './Auth0Login.module.scss';

const dataTestIdPrefix = 'motili-login-page';

const Auth0Login = () => {
    const [busy, setBusy] = useState<boolean>(false);
    const { logout, loginWithRedirect, isAuthenticated } = useAuth0();
    const { clientId } = useAuth0Config();
    const { authenticated: s2Authenticated } = useAppSelector<State>(
        state => state.authentication
    );
    const handleLogin = useCallback(async () => {
        setBusy(true);
        try {
            // redirects to Auth0 login page
            await loginWithRedirect();
        } catch (error) {
            logger.error('Auth0 login redirect failed', error);
            setBusy(false);
            Alert.error(
                'There was an error processing the login request, please contact support@motili.com if you need assistance logging into your account.',
                {
                    autoClose: 3000,
                }
            );
        }
    }, [loginWithRedirect]);

    if (isLoggedIn() && isAuthenticated && s2Authenticated) {
        const redirectPath = fetchSessionRedirect();
        return <Navigate to={`/?redirectTo=${redirectPath}`} />;
    }

    if (!isAuthenticated) {
        return (
            <main
                data-test-id={`${dataTestIdPrefix}-container`}
                className={styles.loginContainer}
                style={{
                    backgroundImage: `url(${Config.baseCDNURL}images/loginPage/Wave-BG-web-large.svg)`,
                }}
            >
                <div className={styles.loginContent}>
                    <div
                        data-test-id={`${dataTestIdPrefix}-form`}
                        className={styles.loginForm}
                    >
                        <img
                            src={`${Config.baseCDNURL}images/loginPage/transparent_new_logo.svg`}
                            className={styles.logo}
                            alt='Motili Logo'
                        />
                        <h1 style={{ fontSize: '16px' }}>
                            Welcome to the Motili Platform
                        </h1>
                        <MotiliButton
                            onClick={handleLogin}
                            text='Continue with SSO Login'
                            moStyle='primary'
                            size='lg'
                            busy={busy}
                            dataTestId={`${dataTestIdPrefix}-login-button-sso`}
                        />
                    </div>
                </div>
            </main>
        );
    }

    // Edge case: logged out of S2 but still logged into Auth0 somehow - logout first then redirect to login
    if (!isLoggedIn()) {
        try {
            logout({
                clientId,
                logoutParams: {
                    returnTo: `${globalThis.location.origin}/#/login`,
                },
            });
        } catch (error) {
            logger.error('Auth0 logout failed', error);
        }
    }

    return <RefreshOverlay />;
};

export default Auth0Login;
